
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  CalendarClock,
  CirclePause,
  CirclePlay,
  Clock3,
  LoaderCircle,
  RefreshCw,
  Search,
  Trash2,
  Webhook,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type AutomationDefinition = {
  automation_key: string;
  display_name?: string;
  description?: string;
  state?: string;
  domain?: string;
  version?: number;
  risk_level?: string;
  runtime_mode?: string;
  trigger_types?: string[];
  owner_only?: boolean;
  approval_required?: boolean;
};

type AutomationRun = {
  run_id?: string;
  automation_key?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
};

type BotWorkspacePayload = {
  contract?: string;
  title?: string;
  automation_definitions?: AutomationDefinition[];
  recent_runs?: AutomationRun[];
  active_runs?: AutomationRun[];
  summary?: Record<string, unknown>;
};

type FilterKey = "ALL" | "ROUTINES" | "SCHEDULES" | "TRIGGERS" | "WEBHOOKS" | "EVENTS" | "WATCHES";

const filters: Array<{ key: FilterKey; label: string }> = [
  { key: "ALL", label: "All" },
  { key: "ROUTINES", label: "Routines" },
  { key: "SCHEDULES", label: "Schedules" },
  { key: "TRIGGERS", label: "Triggers" },
  { key: "WEBHOOKS", label: "Webhooks" },
  { key: "EVENTS", label: "Events" },
  { key: "WATCHES", label: "Watches" },
];

function triggerMatches(definition: AutomationDefinition, filter: FilterKey) {
  if (filter === "ALL") return true;
  const types = new Set((definition.trigger_types ?? []).map((value) => value.toUpperCase()));
  if (filter === "ROUTINES") return types.has("SCHEDULE") || types.has("AUTOMATION");
  if (filter === "SCHEDULES") return types.has("SCHEDULE");
  if (filter === "WEBHOOKS") return types.has("WEBHOOK");
  if (filter === "WATCHES") return types.has("CONDITION");
  if (filter === "EVENTS") {
    return Array.from(types).some((value) => value.endsWith("_EVENT")) || types.has("SYSTEM_EVENT") || types.has("APP_ACTION") || types.has("API_REQUEST");
  }
  if (filter === "TRIGGERS") return Array.from(types).some((value) => value !== "MANUAL");
  return true;
}

function statusTone(state?: string) {
  const value = String(state ?? "UNKNOWN").toUpperCase();
  if (value === "ACTIVE") return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300";
  if (value === "PAUSED") return "bg-amber-500/10 text-amber-700 dark:text-amber-300";
  if (value === "DRAFT") return "bg-foreground/[0.06] text-foreground/55";
  return "bg-primary/10 text-primary";
}

function runForDefinition(runs: AutomationRun[] | undefined, automationKey: string) {
  return (runs ?? []).find((run) => run.automation_key === automationKey) ?? null;
}

export function RunyiAutomationManager({
  supabase,
  connected,
  isAdmin,
  onClose,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<BotWorkspacePayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterKey>("ALL");
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !connected) {
      setError("RUNYI Automations are unavailable while disconnected.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: next, error: invokeError } = await supabase.functions.invoke("runyi-ui-gateway", {
        body: { screen: "BOT_WORKSPACE", mode: "read" },
      });
      if (invokeError) throw invokeError;
      setData((next ?? null) as BotWorkspacePayload | null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "RUNYI Automations are temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  }, [connected, supabase]);

  useEffect(() => {
    void load();
  }, [load]);

  const definitions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (data?.automation_definitions ?? [])
      .filter((definition) => String(definition.state ?? "").toUpperCase() !== "RETIRED")
      .filter((definition) => triggerMatches(definition, filter))
      .filter((definition) => {
        if (!needle) return true;
        return [
          definition.automation_key,
          definition.display_name,
          definition.description,
          definition.domain,
          ...(definition.trigger_types ?? []),
        ].filter(Boolean).join(" ").toLowerCase().includes(needle);
      });
  }, [data?.automation_definitions, filter, query]);

  async function runNow(definition: AutomationDefinition) {
    if (!supabase || !connected || busyKey) return;
    setBusyKey(definition.automation_key);
    setNotice(null);
    setError(null);
    try {
      const { data: result, error: invokeError } = await supabase.functions.invoke("runyi-automation-orchestrator", {
        body: { action: "start", automation_key: definition.automation_key, channel: "MANUAL" },
      });
      if (invokeError || result?.error) throw new Error(result?.error || invokeError?.message || "RUNYI could not start this automation.");
      setNotice(`${definition.display_name ?? definition.automation_key} started through RUNYI.`);
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "RUNYI could not start this automation.");
    } finally {
      setBusyKey(null);
    }
  }

  async function governedLifecycle(definition: AutomationDefinition, nextState: "ACTIVE" | "PAUSED" | "RETIRED") {
    if (!supabase || !connected || busyKey) return;
    if (nextState === "RETIRED" && !window.confirm(`Delete “${definition.display_name ?? definition.automation_key}”? RUNYI will retire it while preserving audit history.`)) return;
    setBusyKey(definition.automation_key);
    setNotice(null);
    setError(null);
    try {
      const verb = nextState === "RETIRED" ? "delete by retiring" : nextState === "PAUSED" ? "pause" : "resume";
      const message = [
        `${verb} the canonical RUNYI automation ${definition.automation_key}.`,
        `Set its lifecycle state to ${nextState} through the registered automation policy management operation.`,
        "Use the Universal Handshake/URP and shared execution lane, preserve audit history, enforce any Owner approval that RUNYI resolves, and verify canonical read-back. Do not mutate automation tables directly.",
      ].join(" ");
      const { data: result, error: invokeError } = await supabase.functions.invoke("runyi-ai", {
        body: { mode: "work", message },
      });
      if (invokeError || result?.error) throw new Error(result?.error || invokeError?.message || "RUNYI could not update this automation.");
      setNotice(result?.output_text || `RUNYI accepted the ${verb} request.`);
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : "RUNYI could not update this automation.");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="absolute inset-0 z-[100] overflow-hidden bg-background">
      <div className="flex h-full min-h-0 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-foreground/8 px-3 py-3 sm:px-4">
          <span className="grid size-10 place-items-center rounded-2xl bg-primary/10 text-primary"><Zap className="size-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Automations</p>
            <p className="truncate text-[10px] text-foreground/42">Routines · schedules · triggers · webhooks · events · condition watches</p>
          </div>
          <Button type="button" variant="ghost" size="icon" className="size-9 rounded-full" onClick={() => void load()} disabled={loading} title="Refresh">
            <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-9 rounded-full" onClick={onClose} aria-label="Close Automations"><X className="size-5" /></Button>
        </header>

        <div className="shrink-0 border-b border-foreground/8 px-3 py-3 sm:px-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-foreground/35" />
            <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search routines, schedules, triggers, webhooks…" className="h-10 rounded-xl border-foreground/10 bg-foreground/[0.025] pl-9 text-xs" />
          </div>
          <div className="scrollbar-none mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
            {filters.map((item) => (
              <button key={item.key} type="button" onClick={() => setFilter(item.key)} className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-semibold transition ${filter === item.key ? "bg-foreground text-background" : "bg-foreground/[0.045] text-foreground/55 hover:bg-foreground/[0.075]"}`}>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4">
          {notice ? <div className="mb-3 rounded-xl border border-primary/15 bg-primary/[0.045] px-3 py-2.5 text-[10px] leading-4 text-foreground/70">{notice}</div> : null}
          {error ? <div className="mb-3 rounded-xl border border-destructive/20 bg-destructive/[0.055] px-3 py-2.5 text-[10px] leading-4 text-destructive">{error}</div> : null}
          {loading && !data ? <div className="grid min-h-52 place-items-center text-xs text-foreground/45"><LoaderCircle className="mr-2 size-4 animate-spin" />Loading authoritative RUNYI automations…</div> : null}

          {!loading && data && definitions.length === 0 ? (
            <div className="mx-auto max-w-md py-14 text-center">
              <Workflow className="mx-auto size-6 text-foreground/30" />
              <p className="mt-3 text-sm font-semibold">No matching automations</p>
              <p className="mt-1 text-xs leading-5 text-foreground/42">Create one conversationally in the RUNYI Bot Workspace and it will appear here automatically.</p>
            </div>
          ) : null}

          <div className="mx-auto max-w-3xl space-y-2.5">
            {definitions.map((definition) => {
              const lastRun = runForDefinition(data?.recent_runs, definition.automation_key) ?? runForDefinition(data?.active_runs, definition.automation_key);
              const busy = busyKey === definition.automation_key;
              const state = String(definition.state ?? "UNKNOWN").toUpperCase();
              return (
                <article key={definition.automation_key} className="rounded-2xl border border-foreground/8 bg-foreground/[0.018] p-3.5 sm:p-4">
                  <div className="flex items-start gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-foreground/[0.045] text-foreground/65">
                      {(definition.trigger_types ?? []).includes("WEBHOOK") ? <Webhook className="size-4" /> : (definition.trigger_types ?? []).includes("SCHEDULE") ? <CalendarClock className="size-4" /> : <Zap className="size-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-xs font-semibold sm:text-sm">{definition.display_name ?? definition.automation_key}</p>
                        <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold ${statusTone(state)}`}>{state}</span>
                      </div>
                      <p className="mt-1 break-all font-mono text-[9px] text-foreground/35">{definition.automation_key}</p>
                      {definition.description ? <p className="mt-2 text-[10px] leading-4 text-foreground/48">{definition.description}</p> : null}
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {(definition.trigger_types ?? ["MANUAL"]).map((trigger) => <span key={trigger} className="rounded-full bg-foreground/[0.05] px-2 py-1 text-[8px] font-medium text-foreground/55">{trigger.replaceAll("_", " ")}</span>)}
                        {definition.domain ? <span className="rounded-full bg-primary/[0.06] px-2 py-1 text-[8px] font-medium text-primary/75">{definition.domain}</span> : null}
                      </div>
                      {lastRun ? <p className="mt-2 flex items-center gap-1.5 text-[9px] text-foreground/38"><Clock3 className="size-3" />Last run: {lastRun.status ?? "UNKNOWN"}{lastRun.created_at ? ` · ${new Date(lastRun.created_at).toLocaleString()}` : ""}</p> : null}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center justify-end gap-1.5 border-t border-foreground/7 pt-3">
                    <button type="button" disabled={busy || state !== "ACTIVE"} onClick={() => void runNow(definition)} className="inline-flex items-center gap-1.5 rounded-lg bg-foreground/[0.055] px-2.5 py-1.5 text-[9px] font-semibold text-foreground/65 hover:bg-foreground/[0.09] disabled:opacity-35">
                      {busy ? <LoaderCircle className="size-3 animate-spin" /> : <CirclePlay className="size-3" />}Run now
                    </button>
                    {isAdmin && state !== "DRAFT" ? (
                      <button type="button" disabled={busy} onClick={() => void governedLifecycle(definition, state === "PAUSED" ? "ACTIVE" : "PAUSED")} className="inline-flex items-center gap-1.5 rounded-lg bg-foreground/[0.055] px-2.5 py-1.5 text-[9px] font-semibold text-foreground/65 hover:bg-foreground/[0.09] disabled:opacity-35">
                        {state === "PAUSED" ? <CirclePlay className="size-3" /> : <CirclePause className="size-3" />}{state === "PAUSED" ? "Resume" : "Pause"}
                      </button>
                    ) : null}
                    {isAdmin ? (
                      <button type="button" disabled={busy} onClick={() => void governedLifecycle(definition, "RETIRED")} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[9px] font-semibold text-destructive/80 hover:bg-destructive/[0.07] disabled:opacity-35">
                        <Trash2 className="size-3" />Delete
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <footer className="shrink-0 border-t border-foreground/8 px-4 py-2.5 text-center text-[9px] leading-4 text-foreground/38">
          Create, change, pause or delete any automation conversationally in the workspace. This menu reads the same canonical RUNYI automation objects.
        </footer>
      </div>
    </div>
  );
}
